/* eslint-disable no-prototype-builtins */
import isEqual from 'lodash/isEqual';
import sliderComponent from '../../generic/sliderComponent';
import Mustache from 'mustache';

//Number formatter for a monthly price
const numberFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Handles Laakkonen financing form tabs
 */
export default class LaakkonenFinancingFormTabs {

  /**
   * @property {HTMLElement} all class instance functionality is scoped under this one element
   * @private
   */
  #element;

  /**
   * @property {HTMLElement[]} elements elements controlled by FinancingFormMain component
   */
  #elements;

  /**
   * @property {object} state object related to this component
   * @private
   */
  #state = {};

  /**
   * @property {CarPageApp} carPageApp car page state manager
   * @private
   */
  #carPageApp;

  /*
 * Initializes the component: renders, binds, starts observers etc...
 * @param {Element} element Element bind class functionality to on
 * @param {CarPageApp} carPageApp optional, if doesn't exist, it's created. (The CarPageApp component is a singleton)
 */
  constructor(element, carPageApp) {
    this.#element = element;
    this.#carPageApp = carPageApp;
    this.findElements();

    if (this.#elements.financingRadioInputs.length) {
      this.bind();
      this.#initState();
    }
  }

  /**
   * Find all elements from dom for accessing later on.
   */
  findElements() {
    this.#elements = {
      financingRadioInputs: Array.from(this.#element.querySelectorAll('#financing-option input, #financing-option-2 input')),
      fiksudiiliPart: this.#element.querySelector('.fiksudiili-financial-part'),
      cashFinancingPart: this.#element.querySelector('.cash-financial-part'),
      downPaymentAndFinalPaymentPart: this.#element.querySelector('.downpayment-and-finalpayment')
    }
  }

  /**
   * Bind all event listeners to DOM
   */
  bind() {
    this.#elements?.financingRadioInputs?.forEach((radioInput) => {
      radioInput.addEventListener('change', this.#financingOptionsChanged);
    })
  }

  /**
   * Called when a user change Financing option radio group value
   * @param {Event} event event object
   */
  #financingOptionsChanged = (event) => {
    this.#carPageApp.updateState('financingFormTabs.activeTab', event.target.value);
  }

  #financingRadioInputsValue() {
    return this.#elements.financingRadioInputs.find(radioInput => radioInput.checked)?.value;
  }

  /**
   * Open a tab content area depending state object
   * @param {object} state current state object
   */
  #openTab(state) {

    //Use a financingOptionRadioGroup value as fall back, usually when a state object not set during the first page load
    const selector = state.financingFormTabs.activeTab ?? this.#financingRadioInputsValue();

    if (selector !== this.#financingRadioInputsValue()) {
      this.#elements.financingRadioInputs.find(radioInput => radioInput.value === selector)?.click();
    }

    const tabAreas = ['.partpayment-part',
      '.cash-financial-part',
      '.fiksudiili-financial-part'];

    tabAreas.filter(tabArea => tabArea !== selector).forEach((tabArea) => {
      this.#element.querySelector(tabArea)?.classList?.toggle('hidden', true);
    });

    const tabArea = this.#element.querySelector(selector);
    tabArea?.classList.toggle('hidden', false);

    //Problem with the slider is that when a slider is hidden and it try to calculate the bar position it fails.. position has to be recalculated after slider is revealed again.
    setTimeout(() => {
      const display = tabArea.querySelector('.generic-slider-display')
      const track = tabArea.querySelector('.generic-slider-track');
      const slider = tabArea.querySelector('input[type="range"]');
      if (display && track && slider) {
        sliderComponent.calculateHeights(display, slider, track);
      }
    }, 500);
  }

  /**
   * Init state manament from a values in local storage. If nothing is found values set in Stage is used.
   * @private
   */
  #initState() {
    const state = this.#filterState(this.#carPageApp.getState());

    if (state?.financingFormTabs) {
      this.#openTab(state);
    }

    if (state) {
      this.#onStateChanges(state);
    }

    this.#carPageApp.registerStateObserver((fullStateObject) => this.#handleStateChanges(fullStateObject));
  }


  /**
   * On state changes this function is called by CarPageApp instance
   * @private
   * @param {object} state filtered state object
   */
  #onStateChanges(state) {

    if (!isEqual(this.#state.partPaymentCalculatorSettings, state.partPaymentCalculatorSettings) && state.partPaymentCalculatorSettings.calculations) {

      const labels = [{ text: 'Osamaksu&shy;rahoituksella', classes: ['label'] }];

      if (state.partPaymentCalculatorSettings.calculations.price) {
        const monthlyPrice = numberFormatter.format(state.partPaymentCalculatorSettings.calculations.price);
        labels.push({ text: `${monthlyPrice}&nbsp;€/kk`, classes: ['label','bold'] });
      }

      this.#setLabelText( this.#elements.financingRadioInputs, '.partpayment-part', labels );
    }

    if (!isEqual(this.#state.fiksugreendiiliCalculatorSettings, state.fiksugreendiiliCalculatorSettings) && state.fiksugreendiiliCalculatorSettings) {

      let labelSelector = '.fiksudiili-financial-part';

      const labels = [{ text: state.carData.fiksudiiliLabel ?? 'FiksuDiilillä', classes: ['label'] }];

      if (state.fiksugreendiiliCalculatorSettings.price) {
        const monthlyPrice = numberFormatter.format(state.fiksugreendiiliCalculatorSettings.price);
        labels.push( { text: `${monthlyPrice}&nbsp;€/kk`, classes: ['label', 'bold'] });
      }

      this.#setLabelText( this.#elements.financingRadioInputs, labelSelector, labels );
    }
  }

  /**
   * Set text label for a radio group option
   * @param {HTMLInputElement[]} inputs radio group inputs
   * @param {string} value checked input value
   * @param {object[]} texts text and CSS-class
   */
  #setLabelText(inputs, value, texts) {
    const textArray = !Array.isArray( texts ) ? [texts] : texts;

    const radioInput = inputs.find( element => {
      return element.value === String(value);
    });

    if( radioInput ) {
      const optionTextSpan = radioInput.closest('label').querySelector('.text')
      optionTextSpan.innerHTML = Mustache.render('{{#texts}}<span class="{{#classes}}{{.}} {{/classes}}">{{{text}}}</span>{{/texts}}', { texts: textArray });
    } else {
      console.warn('No label found from a RadioGroup element with a given value', value)
    }
  }

  /**
   * Filter state object part that is needed in this particular class.
   * @private
   * @param {object} state CarPageApp state object
   * @returns {object|undefined} return if found
   */
  #filterState(state) {
    if (typeof state === 'object' && (state.hasOwnProperty('partPaymentCalculatorSettings') || state.hasOwnProperty('fiksugreendiiliCalculatorSettings') || state.hasOwnProperty('financingFormTabs'))) {
      return {
        carData: state.carData,
        partPaymentCalculatorSettings: state.partPaymentCalculatorSettings,
        fiksugreendiiliCalculatorSettings: state.fiksugreendiiliCalculatorSettings,
        financingFormTabs: state.financingFormTabs
      }
    }
  }

  /**
   * CarPageApp observer function & event listener.
   * @param {object} state full state object
   */
  #handleStateChanges(state) {
    const filteredStateObject = this.#filterState(state);

    if (Object.keys(filteredStateObject).length > 0) {
      this.#onStateChanges(filteredStateObject);
    }

    if (!isEqual(filteredStateObject.financingFormTabs, this.#state.financingFormTabs)) {
      this.#openTab(filteredStateObject);
    }

    this.#state = state;
  }

  /**
   * Static function that is callable from master.js. Instantiate LaakkonenFinancingForm.
   * @public
   * @param {CarPageApp} carPageApp car page state object
   */
  static init(carPageApp) {
    document.querySelectorAll('.laakkonen-financing-form-container').forEach(element => {
      new LaakkonenFinancingFormTabs(element, carPageApp);
    })
  }
}
